import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { useLanguage } from "context/LanguageProvider";


const HeapOperationsGame = () => {
    const [gameState, setGameState] = useState({
        currentLevel: 0,
        score: 0,
        lives: 3,
        completed: false
    });

    const { selLanguage, toggleLanguage, isLoading } = useLanguage();
    
    const [treeNodes, setTreeNodes] = useState([]);
    const [selectedNode, setSelectedNode] = useState(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState("");
    const [isCorrect, setIsCorrect] = useState(false);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const [darkMode] = useState(true);

    useEffect(() => {
        if (!feedbackMessage) return;

        const timer = setTimeout(() => {
            setFeedbackMessage("");
            setShowFeedback(false);
        }, 1000);

        return () => clearTimeout(timer);
    }, [feedbackMessage]);

    const levels = [
    // -------------------------------------------------------------
    // LEVEL 1 — 7 κόμβοι — εύκολο Min-Heap insertion
    // -------------------------------------------------------------
    {
        id: 1,
        type: "insertion",
        heapType: "min",
        title:selLanguage? "Εισαγωγή σε Min-Heap": "Insert into Min-Heap",
        description: selLanguage
            ? "Εισάγουμε τον κόμβο 3 στο Min-Heap. Κάνε κλικ σε 2 κόμβους για να τους εναλλάξεις!"
            : "Insert node 3 into the Min-Heap. Click 2 nodes to swap them!",
        initialTree: [4, 7, 6, 12, 9, 8], // valid min-heap
        newNodes: [3],
        correctTree: [3, 4, 6, 7, 9, 8, 12],
        explanation: "Το 3 ανεβαίνει μέχρι τη ρίζα (bubble up)."
    },

    // -------------------------------------------------------------
    // LEVEL 2 — 7 κόμβοι — εύκολο Max-Heap deletion
    // -------------------------------------------------------------
    {
        id: 2,
        type: "deletion",
        heapType: "max",
        title: selLanguage?"Διαγραφή από Max-Heap":"Delete from Max-Heap",
        description: selLanguage
            ? "Διαγράφουμε τη ρίζα (20). Το τελευταίο στοιχείο πηγαίνει στη ρίζα. Αποκατάστησε την ιδιότητα Max-Heap!"
            : "Delete the root (20). Restore Max-Heap order!",
        initialTree: [20, 15, 18, 10, 12, 13, 17], // valid max-heap
        newNodes: [],
        correctTree: [18, 15, 17, 10, 12, 13],
        explanation: "Το νέο στοιχείο στη ρίζα κάνει sift-down."
    },

    // -------------------------------------------------------------
    // LEVEL 3 — 10 κόμβοι — πιο δύσκολο Min-Heap insertion
    // -------------------------------------------------------------
    {
        id: 3,
        type: "insertion",
        heapType: "min",
        title: selLanguage?"Εισαγωγή σε Min-Heap":"Insert into Min-Heap",
        description: selLanguage
            ? "Εισάγουμε τον κόμβο 2 σε Min-Heap 10 κόμβων. Θα χρειαστούν πολλές ανταλλαγές!"
            : "Insert node 2 into a 10-node Min-Heap. Multiple swaps will be needed!",
        initialTree: [3, 5, 4, 8, 7, 9, 6, 14, 10, 12], // valid min-heap, 10 nodes
        newNodes: [2],
        correctTree: [2, 3, 4, 8, 5, 9, 6, 14, 10, 12, 7],
        explanation: "Το 2 ανεβαίνει σε 3 επίπεδα μέχρι τη ρίζα."
    },

    // -------------------------------------------------------------
    // LEVEL 4 — 12 κόμβοι — Max-Heap deletion με πολλά sift-down
    // -------------------------------------------------------------
    {
        id: 4,
        type: "deletion",
        heapType: "max",
        title: selLanguage?"Διαγραφή από Max-Heap":"Delete from Max-Heap",
        description: selLanguage
            ? "Διαγράφουμε τη ρίζα από Max-Heap 12 κόμβων. Θα χρειαστούν αρκετές ανταλλαγές για να ξαναγίνει έγκυρο!"
            : "Delete the root from this 12-node Max-Heap. Many swaps will be required!",
        initialTree: [40, 35, 38, 30, 32, 36, 37, 25, 28, 29, 31, 34], // valid max-heap
        newNodes: [],
        correctTree: [38, 35, 37, 30, 32, 36, 34, 25, 28, 29, 31],
        explanation: "Το νεο στοιχείο κάνει sift-down μέσα από τρία επίπεδα."
    },

    // -------------------------------------------------------------
    // LEVEL 5 — 15 κόμβοι — δύσκολο Min-Heap insertion με 2 στοιχεία
    // -------------------------------------------------------------
    {
        id: 5,
        type: "insertion",
        heapType: "min",
        title: selLanguage?"Διπλή εισαγωγή σε Min-Heap":"Double Insert into Min-Heap",
        description: selLanguage
            ? "Εισάγουμε τους κόμβους 1 και 4 σε Min-Heap 15 κόμβων! Θέλει πολλές έξυπνες ανταλλαγές."
            : "Insert nodes 1 and 4 into a 15-node Min-Heap! Lots of swaps required.",
        initialTree: [5, 7, 6, 12, 9, 8, 10, 20, 15, 18, 11, 14, 13, 19, 16], // valid min-heap
        newNodes: [1, 4],
        correctTree: [1, 4, 6, 5, 9, 8, 10, 12, 15, 18, 11, 14, 13, 19, 16, 20, 7],
        explanation: "Το 1 ανεβαίνει στη ρίζα — το 4 ανεβαίνει στη δεύτερη θέση."
    },

    // -------------------------------------------------------------
    // LEVEL 6 — 15 κόμβοι — δύσκολη Max-Heap deletion
    // -------------------------------------------------------------
    {
        id: 6,
        type: "deletion",
        heapType: "max",
        title: selLanguage?"Διαγραφή από Max-Heap":"Delete from Max-Heap",
        description: selLanguage
            ? "Διαγράφουμε τη ρίζα από Max-Heap 15 κόμβων. Χρειαζόμαστε πολλά βήματα!"
            : "Delete the root of a 15-node Max-Heap. It needs lots of swaps!",
        initialTree: [70, 65, 68, 60, 62, 67, 66, 55, 58, 61, 59, 63, 64, 57, 56], // valid max-heap
        newNodes: [],
        correctTree: [68, 65, 67, 60, 62, 63, 66, 55, 58, 61, 59, 56, 64, 57],
        explanation: "Το νέο root πρέπει να κατέβει 3–4 επίπεδα μέχρι να σταθεροποιηθεί."
    }
];



    const currentLevel = levels[gameState.currentLevel];

    useEffect(() => {
        initializeLevel();
    }, [gameState.currentLevel]);

    const initializeLevel = () => {
    if (!currentLevel) return;

    let baseTree;

    if (currentLevel.type === "insertion") {
        // αρχικό heap + νέα nodes στο τέλος
        baseTree = [...currentLevel.initialTree, ...currentLevel.newNodes];
    } else {
        // διαγραφή ρίζας: ρίζα φεύγει, το τελευταίο πάει πάνω
        const withoutRoot = currentLevel.initialTree.slice(1);
        const lastElement = withoutRoot[withoutRoot.length - 1];
        const withoutLast = withoutRoot.slice(0, -1);
        baseTree = [lastElement, ...withoutLast];
    }

    // 👉 Για τα μεγαλύτερα levels (4 και πάνω) ανακατεύουμε ΠΙΟ ΠΟΛΥ
    const levelIndex = gameState.currentLevel; // 0-based
    let finalTree = baseTree;

    if (levelIndex >= 3) { 
        // Level 4, 5, 6...
        finalTree = scrambleTree(baseTree, currentLevel.heapType, 10);
    } else if (levelIndex >= 2) {
        // Προαιρετικά: λίγο ανακάτεμα και στο level 3
        finalTree = scrambleTree(baseTree, currentLevel.heapType, 5);
    }

    setTreeNodes(finalTree);
    setShowFeedback(false);
    setSelectedNode(null);
};


    const scrambleTree = (tree, heapType, swaps = 8) => {
    let scrambled = [...tree];

    // κάνουμε μερικά τυχαία swaps
    for (let i = 0; i < swaps; i++) {
        const a = Math.floor(Math.random() * scrambled.length);
        const b = Math.floor(Math.random() * scrambled.length);
        if (a === b) continue;
        [scrambled[a], scrambled[b]] = [scrambled[b], scrambled[a]];
    }

    // αν κατά λάθος βγήκε πάλι έγκυρο heap, κάνε άλλο ένα swap σίγουρα
    if (isValidHeap(scrambled, heapType)) {
        const a = 0;
        const b = scrambled.length - 1;
        [scrambled[a], scrambled[b]] = [scrambled[b], scrambled[a]];
    }

    return scrambled;
};


    const handleNodeClick = (index) => {
        if (selectedNode === null) {
            // Πρώτο κλικ - επιλέγουμε τον κόμβο
            setSelectedNode(index);
        } else {
            // Δεύτερο κλικ - κάνουμε swap
            if (selectedNode !== index) {
                const newTree = [...treeNodes];
                [newTree[selectedNode], newTree[index]] = [newTree[index], newTree[selectedNode]];
                setTreeNodes(newTree);
            }
            setSelectedNode(null);
        }
    };

    const isValidHeap = (tree, heapType) => {
        for (let i = 0; i < tree.length; i++) {
            const leftChild = 2 * i + 1;
            const rightChild = 2 * i + 2;

            if (leftChild < tree.length) {
                if (heapType === 'min' && tree[i] > tree[leftChild]) {
                    return false;
                }
                if (heapType === 'max' && tree[i] < tree[leftChild]) {
                    return false;
                }
            }

            if (rightChild < tree.length) {
                if (heapType === 'min' && tree[i] > tree[rightChild]) {
                    return false;
                }
                if (heapType === 'max' && tree[i] < tree[rightChild]) {
                    return false;
                }
            }
        }
        return true;
    };

    const checkSolution = () => {
        const isCorrectSolution = isValidHeap(treeNodes, currentLevel.heapType);

        setIsCorrect(isCorrectSolution);
        setShowFeedback(true);

        if (isCorrectSolution) {
            setFeedbackMessage(selLanguage ? ` Σωστά! Το δέντρο διατηρεί την ιδιότητα ${currentLevel.heapType === 'min' ? 'Min' : 'Max'}-Heap!` : `Correct! The tree maintains the ${currentLevel.heapType === 'min' ? 'Min' : 'Max'}-Heap property!`);
            setGameState(prev => ({ ...prev, score: prev.score + 100 }));

            setTimeout(() => {
                if (gameState.currentLevel < levels.length - 1) {
                    setGameState(prev => ({ ...prev, currentLevel: prev.currentLevel + 1 }));
                } else {
                    setGameState(prev => ({ ...prev, completed: true }));
                    setShowGameWon(true);
                }
            }, 2000);
        } else {
            setFeedbackMessage(selLanguage ? "Κάποιοι κόμβοι δεν είναι σωστά τοποθετημένοι. Προσπάθησε ξανά" : "Some nodes are not correctly placed. Try again.");

            const newLives = gameState.lives - 1;
            setGameState(prev => ({ ...prev, lives: newLives }));

            if (newLives <= 0) {
                setTimeout(() => {
                    setShowGameOver(true);
                }, 500);
            }
        }
    };

    const resetGame = () => {
        setGameState({
            currentLevel: 0,
            score: 0,
            lives: 3,
            completed: false
        });
        setShowGameOver(false);
        setShowGameWon(false);
        initializeLevel();
    };

    const getNodePositions = (level, nodeIndex) => {
    const baseSpacing = 600;                      // πόσο "απλώνεται" συνολικά το δέντρο οριζόντια
    const levelSpacing = baseSpacing / Math.pow(2, level); // ΜΙΚΡΑΙΝΕΙ όσο κατεβαίνουμε επίπεδο

    const nodesInLevel = Math.pow(2, level);      // θεωρούμε πλήρες επίπεδο (όπως πριν)
    const indexInLevel = nodeIndex - (Math.pow(2, level) - 1);
    const startOffset = -(nodesInLevel - 1) * levelSpacing / 2;

    return {
        x: startOffset + indexInLevel * levelSpacing,
        y: level * 100
    };
};


    const getNodeLevel = (index) => {
        return Math.floor(Math.log2(index + 1));
    };

    const renderEdges = () => {
        const edges = [];

        for (let i = 0; i < treeNodes.length; i++) {
            const leftChildIndex = 2 * i + 1;
            const rightChildIndex = 2 * i + 2;

            const parentLevel = getNodeLevel(i);
            const parentPos = getNodePositions(parentLevel, i);

            if (leftChildIndex < treeNodes.length) {
                const childLevel = getNodeLevel(leftChildIndex);
                const childPos = getNodePositions(childLevel, leftChildIndex);

                edges.push(
                    <line
                        key={`edge-${i}-${leftChildIndex}`}
                        x1={parentPos.x}
                        y1={parentPos.y + 25}
                        x2={childPos.x}
                        y2={childPos.y + 25}
                        stroke="#64748b"
                        strokeWidth="3"
                        opacity={1}
                    />
                );
            }

            if (rightChildIndex < treeNodes.length) {
                const childLevel = getNodeLevel(rightChildIndex);
                const childPos = getNodePositions(childLevel, rightChildIndex);

                edges.push(
                    <line
                        key={`edge-${i}-${rightChildIndex}`}
                        x1={parentPos.x}
                        y1={parentPos.y + 25}
                        x2={childPos.x}
                        y2={childPos.y + 25}
                        stroke="#64748b"
                        strokeWidth="3"
                        opacity={1}
                    />
                );
            }
        }

        return edges;
    };

    const renderNode = (index) => {
        const level = getNodeLevel(index);
        const position = getNodePositions(level, index);
        const nodeValue = treeNodes[index];
        const isSelected = selectedNode === index;

        return (
            <g key={`node-${index}-${nodeValue}`}>
                <motion.circle
                    whileHover={{ scale: 1.15 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ scale: 0 }}
                    animate={{ scale: isSelected ? 1.2 : 1 }}
                    onClick={() => handleNodeClick(index)}
                    cx={position.x}
                    cy={position.y + 25}
                    r="25"
                    fill={isSelected ? "#f59e0b" : "#3b82f6"}
                    stroke={isSelected ? "#fbbf24" : "rgba(255,255,255,0.2)"}
                    strokeWidth={isSelected ? "4" : "2"}
                    style={{
                        cursor: "pointer",
                        filter: isSelected 
                            ? "drop-shadow(0 0 20px rgba(245, 158, 11, 0.8))" 
                            : "drop-shadow(0 6px 15px rgba(59, 130, 246, 0.4))",
                        transition: "all 0.3s ease"
                    }}
                />

                <text
                    x={position.x}
                    y={position.y + 32}
                    textAnchor="middle"
                    fill="white"
                    fontSize="14"
                    fontWeight="bold"
                    style={{ pointerEvents: "none" }}
                >
                    {nodeValue}
                </text>

                <circle
                    cx={position.x}
                    cy={position.y + 25}
                    r="25"
                    fill="transparent"
                    onClick={() => handleNodeClick(index)}
                    style={{ cursor: "pointer" }}
                />
            </g>
        );
    };

    const renderTree = () => {
        const maxLevel = Math.floor(Math.log2(treeNodes.length));
        const svgWidth = 900;
        const svgHeight = (maxLevel + 2) * 100;

        return (
            <div style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                overflowX: "auto",
                marginTop: "30px",
                marginBottom: "30px",
            }}>
                <svg
                    width={svgWidth}
                    height={svgHeight}
                    viewBox={`${-svgWidth / 2} 0 ${svgWidth} ${svgHeight}`}
                    style={{ minWidth: "700px" }}
                >
                    {renderEdges()}
                    {treeNodes.map((_, index) => renderNode(index))}
                </svg>
            </div>
        );
    };


    const containerStyle = {
        minHeight: "100vh",
        background: darkMode ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724ff 100%)" : "#f1f5f9",
        padding: "30px 10px",
        borderRadius: "10px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        overflowX: "hidden",
    };

    return (
        <div style={containerStyle}>
            <GameOverModal isVisible={showGameOver} onPlayAgain={resetGame} />
            <GameWonModal isVisible={showGameWon} onPlayAgain={resetGame} />

            <div style={{ width: "100%", maxWidth: "1200px" }}>
                <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                    <div style={{
                        color: darkMode ? "white" : "#1e293b",
                        padding: "12px 24px",
                        borderRadius: "12px",
                        fontWeight: "700",
                        fontSize: "18px",
                        border: darkMode ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid #1e293b",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px"
                    }}>
                        {selLanguage ? `Επίπεδο: ${gameState.currentLevel + 1}/${levels.length}` : `Level: ${gameState.currentLevel + 1}/${levels.length}`}
                    </div>

                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px"
                    }}>
                        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                            {[1, 2, 3].map((heartNum) => (
                                <div
                                    key={heartNum}
                                    style={{
                                        fontSize: "24px",
                                        filter: gameState.lives >= heartNum
                                            ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                            : "grayscale(100%) opacity(0.3)",
                                        transform: gameState.lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                        transition: "all 0.3s ease"
                                    }}
                                >
                                    ❤️
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{
                        backgroundColor: darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
                        borderRadius: "15px",
                        padding: "20px",
                        marginBottom: "20px",
                        textAlign: "center",
                        color: darkMode ? "white" : "#1e293b"
                    }}
                >
                    <h2 style={{ margin: "0 0 10px 0", fontSize: "1.5rem" }}>{currentLevel?.title}</h2>
                    <p style={{ margin: "0 0 10px 0", lineHeight: "1.6", color: darkMode ? "#94a3b8" : "#64748b" }}>
                        {currentLevel?.description}
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    style={{
                        backgroundColor: darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
                        borderRadius: "20px",
                        padding: "30px",
                        marginBottom: "30px",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255,255,255,0.2)"
                    }}
                >
                    {renderTree()}
                </motion.div>

                <AnimatePresence>
                    {showFeedback && (
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -50 }}
                            style={{
                                backgroundColor: isCorrect ? "rgba(46, 204, 113, 0.2)" : "rgba(231, 76, 60, 0.2)",
                                borderRadius: "15px",
                                padding: "20px",
                                border: `2px solid ${isCorrect ? '#2ecc71' : '#e74c3c'}`,
                                backdropFilter: "blur(10px)",
                                textAlign: "center",
                                marginBottom: "20px",
                                color: "white"
                            }}
                        >
                            <p style={{ fontSize: "1.2rem", margin: 0, lineHeight: "1.6" }}>
                                {feedbackMessage}
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div style={{ display: "flex", justifyContent: "center", gap: "15px" }}>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={checkSolution}
                        style={{
                            backgroundColor: "#10b981",
                            color: "white",
                            padding: "12px 30px",
                            borderRadius: "10px",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "1rem",
                            fontWeight: "bold",
                            boxShadow: "0 4px 15px rgba(16, 185, 129, 0.3)"
                        }}
                    >
                        {selLanguage ? "Έλεγχος Λύσης" : "Check Solution"}
                    </motion.button>

                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={initializeLevel}
                        style={{
                            backgroundColor: "#ef4444",
                            color: "white",
                            padding: "12px 30px",
                            borderRadius: "10px",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "1rem",
                            fontWeight: "bold",
                            boxShadow: "0 4px 15px rgba(239, 68, 68, 0.3)"
                        }}
                    >
                        {selLanguage ? "Επαναφορά" : "Reset"}
                    </motion.button>
                </div>
            </div>
        </div>
    );
};

export default HeapOperationsGame;